import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback } from "react";
import api from "../lib/api";
import { translations, THEMES } from "../i18n";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

function hexToHsl(hex) {
  let r = 0, g = 0, b = 0;
  hex = hex.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  r = parseInt(hex.substring(0, 2), 16) / 255;
  g = parseInt(hex.substring(2, 4), 16) / 255;
  b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyTheme(themeId) {
  const t = THEMES[themeId] || THEMES.cloud_sky;
  const root = document.documentElement;
  const set = (k, v) => root.style.setProperty(k, hexToHsl(v));
  set("--background", t.background);
  set("--foreground", t.foreground);
  set("--card", t.card);
  set("--card-foreground", t.foreground);
  set("--popover", t.card);
  set("--popover-foreground", t.foreground);
  set("--primary", t.primary);
  set("--primary-foreground", t.primaryFg);
  set("--secondary", t.secondary);
  set("--secondary-foreground", t.secondaryFg);
  set("--muted", t.muted);
  set("--muted-foreground", t.mutedFg);
  set("--accent", t.accent);
  set("--accent-foreground", t.accentFg);
  set("--border", t.border);
  set("--input", t.border);
  set("--ring", t.primary);
  set("--cb-gradient-from", t.primary);
  set("--cb-gradient-to", t.accent);
  if (t.mode === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function applyCustomColors(colors) {
  if (!colors) return;
  const root = document.documentElement;
  const setHsl = (k, v) => { if (v) root.style.setProperty(k, hexToHsl(v)); };
  setHsl("--primary", colors.primary);
  setHsl("--ring", colors.primary);
  setHsl("--cb-gradient-from", colors.primary);
  setHsl("--accent", colors.accent);
  setHsl("--cb-gradient-to", colors.accent);
  setHsl("--background", colors.background);
}

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("cb_lang") || "ar");
  const [theme, setThemeState] = useState("cloud_beige");
  const [settings, setSettings] = useState(null);
  const t = translations[lang];
  const dir = lang === "ar" ? "rtl" : "ltr";

  useLayoutEffect(() => {
    applyTheme(theme);
    if (settings?.custom_colors) applyCustomColors(settings.custom_colors);
  }, [theme, settings]);

  useLayoutEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    localStorage.setItem("cb_lang", lang);
  }, [lang, dir]);

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      if (data.theme) setThemeState(data.theme);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { refreshSettings(); }, [refreshSettings]);

  const setLang = (l) => setLangState(l);
  const toggleLang = () => setLangState((p) => (p === "ar" ? "en" : "ar"));

  // localized field helper for DB docs: pick name_ar / name_en
  const L = useCallback((obj, key) => {
    if (!obj) return "";
    return obj[`${key}_${lang}`] || obj[`${key}_ar`] || obj[`${key}_en`] || "";
  }, [lang]);

  return (
    <AppContext.Provider value={{ lang, setLang, toggleLang, dir, t, theme, setTheme: setThemeState, settings, refreshSettings, L }}>
      {children}
    </AppContext.Provider>
  );
}
